import { ComprehensiveTripExport } from './exportService';

export interface ImportResult {
  success: boolean;
  type: 'comprehensive' | 'legacy-trip' | 'legacy-collection';
  data?: ComprehensiveTripExport;
  error?: string;
  metadata?: {
    exportTimestamp?: string;
    tripName?: string;
    travellersCount?: number;
    apartmentsCount?: number;
    signUpsCount?: number;
  };
}

export class ImportService {
  /**
   * Analyzes and validates import data
   */
  static analyzeImportData(rawData: unknown): ImportResult {
    try {
      // Check if it's the new comprehensive format
      if (rawData.exportVersion && rawData.trip && rawData.subcollections) {
        const comprehensiveData = rawData as ComprehensiveTripExport;
        
        return {
          success: true,
          type: 'comprehensive',
          data: comprehensiveData,
          metadata: {
            exportTimestamp: comprehensiveData.exportTimestamp,
            tripName: comprehensiveData.trip.name,
            travellersCount: comprehensiveData.subcollections.travellers.length,
            apartmentsCount: comprehensiveData.subcollections.apartments.length,
            signUpsCount: comprehensiveData.subcollections.signUps.length,
          }
        };
      }
      
      // Check if it's a legacy single trip format
      if (rawData.id && rawData.name && !rawData.length) {
        return {
          success: true,
          type: 'legacy-trip',
          metadata: {
            tripName: rawData.name,
          }
        };
      }
      
      // Check if it's a legacy collection format
      if (Array.isArray(rawData)) {
        return {
          success: true,
          type: 'legacy-collection',
          metadata: {
            tripName: rawData.length > 0 ? `${rawData.length} trips` : 'Empty collection',
          }
        };
      }
      
      return {
        success: false,
        error: 'Unknown import format'
      };
      
    } catch (error) {
      return {
        success: false,
        error: (error as Error).message
      };
    }
  }

  /**
   * Reads and parses a file for import
   */
  static readImportFile(file: File): Promise<ImportResult> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target?.result as string);
          const result = ImportService.analyzeImportData(importedData);
          resolve(result);
        } catch (error) {
          resolve({
            success: false,
            error: `Failed to parse JSON: ${(error as Error).message}`
          });
        }
      };
      
      reader.onerror = () => {
        resolve({
          success: false,
          error: 'Failed to read file'
        });
      };
      
      reader.readAsText(file);
    });
  }

  /**
   * Performs the actual import operation (placeholder for future implementation)
   */
  static async performImport(importData: ComprehensiveTripExport): Promise<boolean> {
    // TODO: Implement actual import logic
    // This would involve:
    // 1. Creating a new trip from importData.trip
    // 2. Creating subcollections for travellers, apartments, signUps
    // 3. Handling conflicts and validation
    
    console.log('Import operation not yet implemented:', {
      trip: importData.trip.name,
      travellers: importData.subcollections.travellers.length,
      apartments: importData.subcollections.apartments.length,
      signUps: importData.subcollections.signUps.length,
    });
    
    return false; // Return false until implementation is complete
  }
}